import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { catchError, from, map, of, skipWhile, switchMap, take } from 'rxjs';
import { UserPermission } from '../models/user-permission';
import { TenantContextService } from '../tenant-context/tenant-context.service';
import { ROUTE_PERMISSIONS, RoutePermission } from '../constants/route-permissions';
import { TenantUserRolePermissionsService } from 'src/app/core/services/application/tenant-user-role-permissions.service';

export const userPermissionsGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
    const tenantContextService = inject(TenantContextService);
    const tenantPermissionsService = inject(TenantUserRolePermissionsService);

    const router = inject(Router);
    const allRequiredPermissions = ROUTE_PERMISSIONS as RoutePermission[];

    // Find the matching path and associated permissions
    const routeConfig = route.routeConfig?.path;
    const requiredPermissions = allRequiredPermissions
        .filter(c => (routeConfig || '').startsWith(c.path))
        .flatMap(m => m.permissions);

    if (requiredPermissions.length == 0 || routeConfig?.length == 0) {
        return true;
    }

    const getUserPermissionsFnc = () => {
        return tenantPermissionsService.getAllPermissions().pipe(
            skipWhile((userPermissions: UserPermission[] | null) => userPermissions === null),
            take(1),
            map((userPermissions: Array<UserPermission> | null) => {
                const missingPermissions: Array<string> = requiredPermissions.filter((requiredPermission: string) => {
                    const tenantPermissions = userPermissions?.find(
                        (userPermission: UserPermission) => userPermission.tenantId === currentTenantId
                    );
                    const hasRequiredPermission = tenantPermissions?.permissions.includes(requiredPermission) ?? false;

                    return !hasRequiredPermission;
                });

                const hasPermission: boolean = missingPermissions.length == 0;

                if (hasPermission) {
                    return true;
                } else {
                    console.group('User is missing the following permissions:');
                    missingPermissions.forEach((permission: string) => console.log(permission));
                    console.groupEnd();

                    return router.createUrlTree(['/not-authorized'], { relativeTo: null });
                }
            })
        );
    };

    let currentTenantId: string | undefined = tenantContextService.currentTenant?.tenantId;
    if (!currentTenantId) {
        return from(tenantContextService.prepTenantContext()).pipe(
            switchMap(() => {
                currentTenantId = tenantContextService.currentTenant?.tenantId;
                if (!currentTenantId) {
                    return of(router.createUrlTree(['/apps']));
                } else {
                    return getUserPermissionsFnc().pipe(catchError(() => of(router.createUrlTree(['/apps']))));
                }
            })
        );
    } else {
        return getUserPermissionsFnc();
    }
};
